// src/components/layout/Navbar.jsx
import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import contactData from "../../data/contactData";

const slideDown = keyframes`
  from { transform: translateY(-100%); opacity: 0; }
  to   { transform: translateY(0);     opacity: 1; }
`;

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.sticky};
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 72px;
  background: ${({ $scrolled, theme }) =>
    $scrolled ? theme.colors.bgNavbar : "transparent"};
  backdrop-filter: ${({ $scrolled }) => ($scrolled ? "blur(16px)" : "none")};
  border-bottom: ${({ $scrolled, theme }) =>
    $scrolled ? `1px solid ${theme.colors.border}` : "1px solid transparent"};
  transition: all 0.4s ease;
  animation: ${slideDown} 0.5s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0 1.5rem;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 1rem;
    height: 64px;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
`;

const LogoImage = styled.img`
  width: 44px;
  height: 44px;
  object-fit: contain;
  display: block;
  border-radius: 30px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 40px;
    height: 40px;
  }
`;

const LogoText = styled.div``;

const LogoName = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-weight: 700;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.1;
`;

const LogoSub = styled.div`
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  color: ${({ theme }) => theme.colors.gold};
  text-transform: uppercase;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  padding: 0.5rem 1rem;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.gold : theme.colors.textSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  position: relative;
  text-decoration: none;

  &::after {
    content: "";
    position: absolute;
    bottom: 2px;
    left: 1rem;
    right: 1rem;
    height: 1px;
    background: ${({ theme }) => theme.colors.gold};
    transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
    transition: transform 0.3s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) => theme.colors.goldMuted};

    &::after {
      transform: scaleX(1);
    }
  }
`;

const CallBtn = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.gold},
    ${({ theme }) => theme.colors.goldLight}
  );
  color: #1a1000;
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  border-radius: ${({ theme }) => theme.borderRadius.button};
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.normal};
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.gold};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const HamburgerBtn = styled.button`
  display: none;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textPrimary};
  width: 42px;
  height: 42px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  font-size: 1rem;

  &:hover {
    border-color: ${({ theme }) => theme.colors.gold};
    color: ${({ theme }) => theme.colors.gold};
    background: ${({ theme }) => theme.colors.goldMuted};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: flex;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  inset: 0;
  top: 64px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  z-index: ${({ theme }) => theme.zIndex.sticky - 1};
  display: flex;
  flex-direction: column;
  padding: 2rem 1.5rem;
  gap: 0.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  transform: ${({ $open }) => ($open ? "translateX(0)" : "translateX(100%)")};
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
`;

const MobileLink = styled(Link)`
  padding: 1rem;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-family: ${({ theme }) => theme.fonts.display};
  font-weight: 600;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.gold : theme.colors.textSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-decoration: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) => theme.colors.goldMuted};
    padding-left: 1.5rem;
  }
`;

const MobileCallBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin-top: 1.5rem;
  padding: 1rem;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.gold},
    ${({ theme }) => theme.colors.goldLight}
  );
  color: #1a1000;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes.base};
  border-radius: ${({ theme }) => theme.borderRadius.button};
  text-decoration: none;
`;

const NAV_LINKS = [
  { label: "Services", to: "/services" },
  { label: "Gallery", to: "/gallery" },
  { label: "About & Contact", to: "/about" },
  { label: "Careers", to: "/careers" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <Nav $scrolled={scrolled}>
        <Logo
          to="/"
          onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
        >
          <LogoImage src="/logo-image.png" alt="Metrocrafts Interiors logo" />
          <LogoText>
            <LogoName>Metrocrafts</LogoName>
            <LogoSub>Interiors</LogoSub>
          </LogoText>
        </Logo>

        <NavLinks>
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} $active={location.pathname === l.to}>
              {l.label}
            </NavLink>
          ))}
        </NavLinks>

        <CallBtn href={contactData.phoneHref}>
          <FontAwesomeIcon icon={faPhone} />
          Call Now
        </CallBtn>

        <HamburgerBtn onClick={() => setMenuOpen((p) => !p)}>
          <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
        </HamburgerBtn>
      </Nav>

      <MobileMenu $open={menuOpen}>
        {NAV_LINKS.map((l) => (
          <MobileLink key={l.to} to={l.to} $active={location.pathname === l.to}>
            {l.label}
          </MobileLink>
        ))}
        <MobileCallBtn href={contactData.phoneHref}>
          <FontAwesomeIcon icon={faPhone} />
          Call {contactData.phoneDisplay}
        </MobileCallBtn>
      </MobileMenu>
    </>
  );
};

export default Navbar;
